import { useTranslate } from "@refinedev/core";
import { useSession } from "hooks/useSession";
import { useNavigate } from "react-router-dom";
import { useAntTheme } from "hooks/useAntTheme";
import { useProjectWizardStore } from "../../projects/useProjectWizardStore";
import { useAnalytics } from "hooks/useAnalytics";
import { AnalyticsEvents } from "types/analytics";
import { useSoftPaywall } from "@/providers/SoftPaywallProvider";
import { useCreateExperienceEntry } from "hooks/useCreateExperienceEntry";
import styled from "styled-components";
import { FileVideoIcon, LightningIcon } from "@phosphor-icons/react";
import { radius } from "@libertify/ui";

export type WelcomeHeaderProps = {
  isProjectQuotaReached?: boolean;
};

export const WelcomeHeader = ({
  isProjectQuotaReached = false,
}: WelcomeHeaderProps) => {
  const t = useTranslate();
  const { user } = useSession();
  const navigate = useNavigate();
  const { theme } = useAntTheme();
  const { setWizardStartTime } = useProjectWizardStore();
  const { trackEvent } = useAnalytics();
  const { paywallFlow } = useSoftPaywall();
  const { startCreateExperience, chooserModal } = useCreateExperienceEntry();

  const handleNewExperience = () => {
    setWizardStartTime(Date.now());
    trackEvent(AnalyticsEvents.ProjectCreateInit, { page: "home", paywall_flow: paywallFlow });
    startCreateExperience();
  };

  const handleUpgrade = () => {
    navigate("/subscription");
  };

  const displayName = user?.first_name || user?.last_name || user?.email;

  return (
    <StyledWelcomeHeader>
      <div>
        <StyledWelcomeTitle>
          {t("pages.home.index.welcomeBack")}
          <br />
          {displayName}
        </StyledWelcomeTitle>
      </div>
      {isProjectQuotaReached ? (
        <StyledUpgradeButton onClick={handleUpgrade}>
          <LightningIcon size={24} />
          {t("pages.home.index.upgradeToCreateNewVideo")}
        </StyledUpgradeButton>
      ) : (
        <StyledNewExperienceButton
          onClick={handleNewExperience}
          $primaryColor={theme.colorPrimary}
        >
          <FileVideoIcon size={24} />
          {t("pages.home.index.newVideoExperience")}
        </StyledNewExperienceButton>
      )}
      {chooserModal}
    </StyledWelcomeHeader>
  );
};

const StyledWelcomeHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 24px 0;

  @media (width <= 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const StyledWelcomeTitle = styled.h1`
  font-family: "Poppins", sans-serif;
  font-size: 48px;
  font-style: normal;
  font-weight: 600;
  line-height: 118%; /* 56.64px */
  letter-spacing: -1.92px;
  color: #090e19;
  margin: 0;

  @media (width <= 768px) {
    font-size: 28px;
  }
`;

const StyledNewExperienceButton = styled.button<{ $primaryColor: string }>`
  display: flex;
  width: 252px;
  max-height: 40px;
  padding: 12px 24px 12px 18px;
  justify-content: center;
  align-items: center;
  gap: 2px;
  border-radius: ${radius.pill}px;
  border: 2px solid #2faaa2;
  background: #67dfd7;
  color: #0c2a28;
  font-family: "Inter", sans-serif;
  font-size: 15px;
  font-style: normal;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: opacity 0.2s;
  line-height: 16px;
  letter-spacing: -0.15px;

  &:hover {
    opacity: 0.85;
  }

  @media (width <= 768px) {
    padding: 10px 20px;
    font-size: 14px;
  }
`;

const StyledUpgradeButton = styled.button`
  display: flex;
  max-height: 40px;
  padding: 12px 24px 12px 18px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: ${radius.pill}px;
  border: 3px solid #3bd4ca;
  background: linear-gradient(92deg, #6946d5 22.22%, #5423ec 80.77%);
  color: #ffffff;
  font-family: "Inter", sans-serif;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: opacity 0.2s;
  line-height: 16px;
  letter-spacing: -0.15px;

  &:hover {
    opacity: 0.9;
  }

  @media (width <= 768px) {
    padding: 10px 20px;
    font-size: 14px;
  }
`;
